import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';

import { useAuthentication } from '@givebacks/api';
import {
  ConsumerCauseBackerResponse,
  useBackerHasStudentFeesAccess,
  useShowBackerConsumerCause,
} from '@givebacks/api/backer';
import {
  IListUserCauseSelectionsResponse,
  useAddCauseSelection,
  useListUserCauseSelections,
} from '@givebacks/api/core';
import { getErrorMessage_v2 } from 'core/Utils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Center,
  Collapse,
  Group,
  Modal,
  ModalProps,
  Progress,
  Stack,
  Text,
  ThemeIcon,
  Title,
  notifications,
} from 'ui';
import { useMatches } from 'ui/Hooks';
import { IconFees } from 'ui/Icons';

import { useNavigateToAuth } from '../../../../hooks/useNavigateToAuth/useNavigateToAuth';
import { useCausePageContext } from '../../CauseContext';

type StudentFeesModalProps = ModalProps & {
  isAccessLoading: boolean;
};

const StudentFeesAccessModal = ({
  opened,
  onClose,
  isAccessLoading = true,
  ...props
}: StudentFeesModalProps) => {
  const navigate = useNavigate();
  const { cause } = useCausePageContext();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isAccessLoading ? 'Student Fees' : undefined}
      {...props}
    >
      <Stack>
        <Text>
          {isAccessLoading ? (
            <Stack>
              <Text fz="sm" fw="600">
                Hang tight—we&apos;re getting things ready!
              </Text>
              <Progress color="cobalt" animated value={100} />
              <Text c="gray" fz="xxs">
                Student Fees are on the way. This should only take a few seconds.
              </Text>
            </Stack>
          ) : (
            <Stack gap="lg">
              <Center>
                <ThemeIcon bg="none" miw="64" h="64">
                  <IconFees size="64" />
                </ThemeIcon>
              </Center>
              <Stack gap="xs">
                <Title order={3}>You&apos;re all set!</Title>
                <Text c="gray" fz="sm">
                  You’re now following{' '}
                  <Text span fw={600} fz="inherit">
                    {cause.name}
                  </Text>
                  , and student fees are unlocked.
                </Text>
                <Text c="gray" fz="sm">
                  View and pay fees anytime from the{' '}
                  <Text span fw={600} fz="inherit">
                    Fees
                  </Text>{' '}
                  tab in your navigation bar.
                </Text>
              </Stack>
            </Stack>
          )}
        </Text>
      </Stack>
      <Collapse in={!isAccessLoading}>
        <Modal.AffixedFooter>
          <Button
            fullWidth
            onClick={() => {
              onClose();
              navigate('/student-fees');
            }}
          >
            Continue
          </Button>
        </Modal.AffixedFooter>
      </Collapse>
    </Modal>
  );
};

const selectCauseHasStudentFees = (data: ConsumerCauseBackerResponse['cause']) => {
  return data.student_fees_access;
};

/**
 * AboutStudentFeesCard
 *
 * - When a org offers Student Fees, this card appears with a CTA to access or manage fees.
 * - If the user isn’t signed in and taps the CTA, they’re prompted to sign in and are returned
 *   to the same org to continue.
 * - If the user is signed in and already has Student Fees unlocked (or is already following the
 *   org), they’re taken straight to the Student Fees area.
 * - If the user is signed in but hasn’t unlocked Student Fees for this org yet, a brief modal
 *   appears while access is enabled. When ready, the modal confirms success and the user can
 *   continue into Student Fees.
 * - The button label adapts ("Access" vs "Manage"), and a loading state is shown when access is
 *   being enabled.
 */
const AboutStudentFeesCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthentication();
  const { handleNavigateToAuth } = useNavigateToAuth();
  const { cause } = useCausePageContext();
  const { refetch: refetchUserCauseSelections } = useListUserCauseSelections();
  const { data: causeHasStudentFees } = useShowBackerConsumerCause({
    causeId: cause.uuid,
    include: 'student_fees_access',
    select: selectCauseHasStudentFees,
  });

  const [showStudentFeesModal, setShowStudentFeesModal] = useState(false);
  const initiatingRef = useRef(false);

  const { hasStudentFeesAccess, refetch: refetchHasStudentFeesAccess } =
    useBackerHasStudentFeesAccess();

  const {
    mutate: addCauseSelection,
    mutateAsync: addCauseSelectionAsync,
    isPolling,
    isPending,
  } = useAddCauseSelection();

  const wrap: ComponentProps<typeof Group>['wrap'] = useMatches({
    base: 'wrap',
    sm: 'nowrap',
  });

  const getIsFavorited = useCallback(
    (userCauses: IListUserCauseSelectionsResponse['cause_selections']) =>
      (userCauses ?? []).map((selection) => selection.cause_id).includes(cause.uuid),
    [cause.uuid]
  );

  const initiateStudentFeesAccess = useCallback(async () => {
    if (initiatingRef.current) return;

    initiatingRef.current = true;

    try {
      const [{ data: selections }, { data: hasAccessNow }] = await Promise.all([
        refetchUserCauseSelections(),
        refetchHasStudentFeesAccess(),
      ]);
      const isFavorited = getIsFavorited(selections ?? []);

      // if the cause is already favorited, navigate to student fees
      if (isFavorited) {
        return navigate('/student-fees');
      }

      // if the cause has access to student fees, navigate to student fees
      if (hasAccessNow) {
        // Favoriting in the background if needed, then navigate immediately
        addCauseSelection(
          { causeId: cause.uuid },
          {
            onError: (error) => {
              notifications.show({
                message: getErrorMessage_v2(error),
                variant: 'error',
              });
            },
          }
        );
        return navigate('/student-fees');
      }

      // No access yet: open modal first, then initiate mutation and wait
      setShowStudentFeesModal(true);

      try {
        await addCauseSelectionAsync({ causeId: cause.uuid });
      } catch (error) {
        notifications.show({
          message: getErrorMessage_v2(error),
          variant: 'error',
        });
      }
    } catch (error) {
      notifications.show({
        message: getErrorMessage_v2(error),
        variant: 'error',
      });
    } finally {
      initiatingRef.current = false;
    }
  }, [
    cause.uuid,
    addCauseSelection,
    addCauseSelectionAsync,
    refetchUserCauseSelections,
    refetchHasStudentFeesAccess,
    getIsFavorited,
    navigate,
    setShowStudentFeesModal,
  ]);

  const onClick = useCallback(() => {
    if (!isAuthenticated) {
      return handleNavigateToAuth({
        page: 'sign-in',
        redirect: `/causes/${cause.uuid}`,
        additionalParams: {
          action: 'student-fees-access',
        },
      });
    }

    void initiateStudentFeesAccess();
  }, [isAuthenticated, initiateStudentFeesAccess, handleNavigateToAuth, cause.uuid]);

  useEffect(() => {
    const action = searchParams.get('action');

    if (isAuthenticated && action === 'student-fees-access') {
      // remove all search params on put the current location on the stack
      navigate(location.pathname, { replace: true });

      void initiateStudentFeesAccess();
    }
  }, [location.pathname, isAuthenticated, searchParams, initiateStudentFeesAccess, navigate]);

  if (!causeHasStudentFees) return null;

  return (
    <Card bg="cobalt.0" bd="1px solid cobalt.1">
      <Group justify="space-between" wrap={wrap}>
        <Group wrap="nowrap">
          <ThemeIcon bg="none" miw={48}>
            <IconFees size="48" />
          </ThemeIcon>
          <Stack gap="4">
            <Text fz="xs" fw="600">
              Student Fees are available for this school!
            </Text>
            <Text fz="xxs" c="gray">
              From sports and clubs to activities, manage all your student’s fees in one place.
            </Text>
          </Stack>
        </Group>

        <Button
          size="sm"
          w={{ base: '100%', sm: 'auto' }}
          flex="1 0 auto"
          loading={isPolling || isPending}
          onClick={onClick}
        >
          {hasStudentFeesAccess ? 'Manage' : 'Access'} Student Fees
        </Button>
      </Group>

      <StudentFeesAccessModal
        opened={showStudentFeesModal}
        isAccessLoading={isPending || isPolling}
        onClose={() => setShowStudentFeesModal(false)}
      />
    </Card>
  );
};

export { AboutStudentFeesCard };
